import React, { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import Image from "next/image";

// Animation variants for elements
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const imageVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: "easeOut",
    },
  },
};

const buttonVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  hover: {
    scale: 1.1,
    boxShadow: "0px 4px 10px rgba(255, 174, 0, 0.3)",
    transition: {
      duration: 0.2,
    },
  },
};

const OverlapSection = () => {
  const sectionRef = useRef(null);

  // Scroll-based animations
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Parallax and scale effects for background images
  const yBackground = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const scaleBackground = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const opacityBackground = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.5, 1, 0.8]
  );

  // Parallax effect for mentor cards
  const yLeftMentor = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const yRightMentor = useTransform(scrollYProgress, [0, 1], [30, -30]);
  const yTestimonial = useTransform(scrollYProgress, [0, 1], [70, -70]);

  // Smooth spring-based transitions
  const springYLeft = useSpring(yLeftMentor, { stiffness: 100, damping: 20 });
  const springYRight = useSpring(yRightMentor, { stiffness: 100, damping: 20 });
  const springYTestimonial = useSpring(yTestimonial, {
    stiffness: 100,
    damping: 20,
  });

  return (
    <section className="relative w-full py-8 overflow-hidden" ref={sectionRef}>
      <motion.div
        className="relative w-full mx-auto max-md:max-w-7xl max-md:px-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, amount: 0.2 }}
      >
        {/* Header */}
        <motion.div
          className="flex flex-col items-center gap-3.5 mb-12 max-md:mb-6"
          variants={itemVariants}
        >
          <h2 className="font-bricolage font-bold text-yellow text-[50px] text-center tracking-[-0.50px] max-md:text-[32px]">
            Our Mentors
          </h2>
          <p className="font-bricolage font-semibold text-yellow text-3xl text-center tracking-[-0.30px] max-md:text-xl">
            &quot;The best journeys are made with the right people beside
            you.&quot;
          </p>
        </motion.div>

        {/* Background and Decorative Images */}
        <div className="relative w-full">
          <div className="relative max-md:flex max-md:flex-col max-md:items-start">
            <Image
              width={302}
              height={799}
              src="/group.svg"
              alt=""
              className="max-md:w-[200px] max-md:h-auto"
              variants={imageVariants}
            />
            <Image
              width={517}
              height={239}
              className="absolute top-[200px] left-[223px] max-md:static max-md:w-[200px] max-md:h-auto max-md:mt-4"
              src="/Vector.svg"
              alt=""
              variants={imageVariants}
            />

            {/* Left Mentor */}
            <motion.div
              className="absolute bottom-[20px] left-[500px] max-md:static max-md:mt-1"
              variants={itemVariants}
              style={{ y: springYLeft }}
            >
              <div className="bg-transparent border-none">
                <CardContent className="p-0 relative">
                  <Image
                    width={397}
                    height={475}
                    className="object-cover max-md:w-full max-md:max-w-[300px] max-md:mx-auto"
                    src="/Tanuja.svg"
                    alt="Ms. Tanuja Sharma"
                    variants={imageVariants}
                    style={{ scale: scaleBackground }}
                  />
                  <motion.div
                    className="absolute bottom-[100px] left-[-155px] flex flex-col w-[242px] items-start gap-[15px] max-md:static max-md:w-full max-md:max-w-[300px] max-md:mt-4 max-md:items-center max-md:text-center"
                    variants={containerVariants}
                  >
                    <h3 className="font-bricolage font-bold text-[#FFAE00] text-[22px] max-md:text-lg">
                      Ms. Tanuja Sharma
                    </h3>
                    <p className="font-jakarta font-light text-white text-[22px] max-md:text-base">
                      Embrace the Serenity: Discover Your Soul&apos;s Journey to
                      Inner Peace
                    </p>
                    <motion.div variants={buttonVariants} whileHover="hover">
                      <Button
                        variant="outline"
                        className="bg-[#1d1d1d] text-white border-[#FFFFFFE680] rounded font-bricolage font-medium text-[15px] [-webkit-text-stroke:0.5px_#000000] max-md:text-sm"
                      >
                        Read more
                      </Button>
                    </motion.div>
                  </motion.div>
                </CardContent>
              </div>
            </motion.div>
          </div>

          {/* Right Mentor */}
          <motion.div
            className="absolute w-[596px] h-[385px] top-[60px] right-10 max-md:static max-md:w-full max-md:h-auto max-md:mt-8"
            variants={itemVariants}
            style={{ y: springYRight }}
          >
            <div className="bg-transparent border-none">
              <CardContent className="p-0 relative">
                <Image
                  width={406}
                  height={385}
                  src="/Harka.svg"
                  alt="Mr. Harka Gurung"
                  className="object-cover max-md:w-full max-md:max-w-[300px] max-md:mx-auto"
                  variants={imageVariants}
                  style={{ scale: scaleBackground }}
                />
                <motion.div
                  className="absolute top-[79px] right-0 flex flex-col w-[235px] items-start gap-[9px] max-md:static max-md:w-full max-md:max-w-[300px] max-md:mt-4 max-md:items-center max-md:text-center"
                  variants={containerVariants}
                >
                  <h3 className="font-bricolage font-bold text-[#FFAE00] text-[22px] max-md:text-lg">
                    Mr. Harka Gurung
                  </h3>
                  <p className="font-bricolage font-normal text-white text-lg h-[116px] max-md:text-base max-md:h-auto">
                    Meet Mr. Harka Gurung, a travel agency producer with an
                    unmatched passion for curating unforgettable journeys around
                    the globe.
                  </p>
                </motion.div>
                <motion.div variants={buttonVariants} whileHover="hover">
                  <Button
                    variant="outline"
                    className="absolute bottom-[60px] right-[117px] bg-[#1d1d1d] text-white border-[#FFFFFFE680] rounded font-bricolage font-medium text-[15px] [-webkit-text-stroke:0.5px_#000000] max-md:static max-md:mt-2 max-md:mx-auto max-md:text-sm"
                  >
                    Read more
                  </Button>
                </motion.div>
              </CardContent>
            </div>
          </motion.div>

          {/* Testimonial */}
          <motion.div
            className="absolute bottom-[20px] right-10 w-[552px] flex flex-col gap-[10px] max-md:static max-md:w-full max-md:max-w-[300px] max-md:mt-8 max-md:mx-auto"
            variants={itemVariants}
            style={{ y: springYTestimonial }}
          >
            {/* Profile Row */}
            <motion.div
              className="flex items-center gap-[20px] max-md:justify-center"
              variants={containerVariants}
            >
              <Image
                width={120}
                height={120}
                className="object-cover max-md:w-[80px] max-md:h-[80px]"
                src="/Tanishka.svg"
                alt="Client Testimonial"
                variants={imageVariants}
                style={{ scale: scaleBackground }}
              />
              <h3 className="font-bricolage font-bold text-[#FFAE00] text-[22px] max-md:text-lg">
                Mr. Tanishka Ahuja
              </h3>
            </motion.div>

            {/* Testimonial Text */}
            <motion.p
              className="font-jakarta font-normal text-[22px] text-[#f2f2f2] max-md:text-base max-md:text-center"
              variants={itemVariants}
            >
              &quot;Traveling with <span className="font-medium">Botique</span>{" "}
              was an absolute delight! From the moment we embarked on our
              journey, we were treated to exceptional service, breathtaking
              sights, and unforgettable experiences.&quot;
            </motion.p>

            {/* Read More Button */}
            <motion.div
              whileHover="hover"
              className="max-md:flex max-md:justify-center"
            >
              <Button
                variant="outline"
                className="bg-[#1d1d1d] w-fit text-white border-[#FFFFFFE680] rounded font-bricolage font-medium text-[15px] [-webkit-text-stroke:0.5px_#000000] max-md:text-sm"
              >
                Read more
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default OverlapSection;
